import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Pencil, Loader2, Search, Clock, DollarSign, Users } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

export default function EmployeeSchedulesPage() {
  const [users, setUsers] = useState([]);
  const [schedules, setSchedules] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    standard_hours_per_day: 8,
    standard_hours_per_week: 40,
    overtime_allowed: true,
    pay_rate: 0,
    overtime_multiplier: 1.5,
    working_days: [0, 1, 2, 3, 4],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, schedulesRes] = await Promise.all([
        axios.get(`${API_URL}/api/users`),
        axios.get(`${API_URL}/api/employee-schedules`),
      ]);
      setUsers(usersRes.data);
      
      // Create a map of user_id -> schedule
      const scheduleMap = {};
      schedulesRes.data.forEach(s => {
        scheduleMap[s.user_id] = s;
      });
      setSchedules(scheduleMap);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user) => {
    const schedule = schedules[user.id];
    setEditingUser(user);
    setFormData({
      standard_hours_per_day: schedule?.standard_hours_per_day ?? 8,
      standard_hours_per_week: schedule?.standard_hours_per_week ?? 40,
      overtime_allowed: schedule?.overtime_allowed ?? true,
      pay_rate: schedule?.pay_rate ?? 0,
      overtime_multiplier: schedule?.overtime_multiplier ?? 1.5,
      working_days: schedule?.working_days || [0, 1, 2, 3, 4],
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.put(`${API_URL}/api/employee-schedules/${editingUser.id}`, formData);
      toast.success('Schedule updated successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleWorkingDay = (day) => {
    setFormData(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day].sort()
    }));
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Employee Schedules</h1>
          <p className="text-muted-foreground">Manage working hours, overtime, and pay rates</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Employees</CardTitle>
            <Users className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{users.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">With Schedules</CardTitle>
            <Clock className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{Object.keys(schedules).length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg Pay Rate</CardTitle>
            <DollarSign className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              ${Object.values(schedules).length > 0 
                ? (Object.values(schedules).reduce((sum, s) => sum + (s.pay_rate || 0), 0) / Object.values(schedules).length).toFixed(2)
                : '0.00'}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-employees-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Hours/Day</TableHead>
                <TableHead>Hours/Week</TableHead>
                <TableHead>Pay Rate</TableHead>
                <TableHead>Overtime</TableHead>
                <TableHead>Working Days</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => {
                const schedule = schedules[user.id];
                return (
                  <TableRow key={user.id} data-testid={`schedule-row-${user.id}`}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{user.name}</div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>{schedule?.standard_hours_per_day || 8}h</TableCell>
                    <TableCell>{schedule?.standard_hours_per_week || 40}h</TableCell>
                    <TableCell>
                      {schedule?.pay_rate ? `$${schedule.pay_rate.toFixed(2)}/hr` : '-'}
                    </TableCell>
                    <TableCell>
                      {schedule?.overtime_allowed !== false ? (
                        <Badge variant="outline" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          {schedule?.overtime_multiplier ?? 1.5}x
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                          Not Allowed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-0.5">
                        {DAYS_OF_WEEK.map(day => (
                          <span
                            key={day.value}
                            className={`text-xs w-5 h-5 flex items-center justify-center rounded ${
                              (schedule?.working_days || [0, 1, 2, 3, 4]).includes(day.value)
                                ? 'bg-primary/20 text-primary'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {day.label[0]}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(user)}
                        data-testid={`edit-schedule-${user.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No employees found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Schedule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Schedule</DialogTitle>
            <DialogDescription>
              Configure working hours and pay rate for {editingUser?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hours_per_day">Hours/Day</Label>
                <Input
                  id="hours_per_day"
                  type="number"
                  step="0.5"
                  min="1"
                  max="24"
                  value={formData.standard_hours_per_day}
                  onChange={(e) => setFormData({ ...formData, standard_hours_per_day: parseFloat(e.target.value) })}
                  data-testid="hours-per-day-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hours_per_week">Hours/Week</Label>
                <Input
                  id="hours_per_week"
                  type="number"
                  step="0.5"
                  min="1"
                  max="168"
                  value={formData.standard_hours_per_week}
                  onChange={(e) => setFormData({ ...formData, standard_hours_per_week: parseFloat(e.target.value) })}
                  data-testid="hours-per-week-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pay_rate">Pay Rate ($/hr)</Label>
                <Input
                  id="pay_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.pay_rate}
                  onChange={(e) => setFormData({ ...formData, pay_rate: parseFloat(e.target.value) || 0 })}
                  data-testid="pay-rate-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="overtime_multiplier">OT Multiplier</Label>
                <Input
                  id="overtime_multiplier"
                  type="number"
                  step="0.1"
                  min="0"
                  max="3"
                  value={formData.overtime_multiplier}
                  onChange={(e) => setFormData({ ...formData, overtime_multiplier: parseFloat(e.target.value) || 0 })}
                  disabled={!formData.overtime_allowed}
                  data-testid="overtime-multiplier-input"
                />
                <p className="text-xs text-muted-foreground">0 = same rate, 1.5 = time and a half</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="overtime_allowed">Allow Overtime</Label>
              <Switch
                id="overtime_allowed"
                checked={formData.overtime_allowed}
                onCheckedChange={(checked) => setFormData({ ...formData, overtime_allowed: checked })}
                data-testid="overtime-allowed-switch"
              />
            </div>

            <div className="space-y-2">
              <Label>Working Days</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <label
                    key={day.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                      formData.working_days.includes(day.value)
                        ? 'bg-primary/10 border-primary'
                        : 'bg-background border-border hover:bg-muted'
                    }`}
                  >
                    <Checkbox
                      checked={formData.working_days.includes(day.value)}
                      onCheckedChange={() => toggleWorkingDay(day.value)}
                    />
                    <span className="text-sm">{day.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-schedule-btn">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Schedule'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
